import React, { useMemo } from "react";
import { Button, type Column } from "@racoon/ui";
import { useT, useDateFnsLocale, useLang, Language, Namespace } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import {
  formatUsage,
  fuelPerDistance,
  isShiftPlanned,
  isShiftTerminal,
  NO_DATA,
  shiftStatusLabel,
} from "../helpers";
import { formatShiftDuration, shiftDurationMinutes } from "../lib/shift-duration";
import { IcPrint } from "@racoon/icons";
import { ShiftStatusBadge } from "./ShiftStatusBadge";
import { DateTimeCell, TriggerHyperlink, UsageCell } from "./shift-cells";
import { ShiftPlate } from "../ui/shift-plate";
import { ShiftDriverModal, type DriverChangeContext } from "../ui/shift-driver-modal";
import { shiftTypeLabel } from "../helpers/shiftGroups";
import { SHIFT_VIEWS, type ShiftsView } from "../model/use-shifts-view";
import type { ShiftData } from "../model/shift-data";

export type ShiftColumnHandlers = {
  onCompleteShift?: (shift: ShiftData) => void;
  onApproveShift?: (shift: ShiftData) => void;
  onPrintWaybill?: (shift: ShiftData) => void;
  /** Per shift, not per screen — one shift starting must not disable the rest. */
  isShiftBusy?: (shiftId: string) => boolean;
  /** Wording only — `isShiftBusy` is what disables. */
  isCompletingShift?: (shiftId: string) => boolean;
  /** Absent while the fleet is still loading; the name then stays plain text. */
  driverChange?: DriverChangeContext;
};

/** What the columns need from the view they are rendered in. */
export type ShiftColumnView = {
  view: ShiftsView;
  /** Shows a row's truck on the dashboard map, from its plate in the driver view. */
  onPlateClick: (truckId: bigint) => void;
  /** The fleet, keyed by String(truck id): a row carries only the id, the change-driver modal needs the vehicle. */
  trucksById: Map<string, ConnectProto.Truck>;
};

const getStatusPriority = (status: ConnectProto.DriverOnShiftStatus) => {
  if (isShiftTerminal(status)) return 1;
  if (isShiftPlanned(status)) return 2;
  return 3;
};

/**
 * Columns of the shifts table, shared by every group. The truck view names the
 * driver per row; the driver view names the truck instead.
 */
export const useShiftColumns = (
  {
    onCompleteShift,
    onApproveShift,
    onPrintWaybill,
    isShiftBusy,
    isCompletingShift,
    driverChange,
  }: ShiftColumnHandlers,
  { view, onPlateClick, trucksById }: ShiftColumnView,
) => {
  const t = useT();
  const dateFnsLocale = useDateFnsLocale();
  const { lang } = useLang();

  return useMemo(() => {
    const columns: Column<ShiftData>[] = [
      {
        id: "id",
        header: t(Namespace.Autopark, "table.shifts.id"),
        width: 132,
        sortable: true,
        accessor: (row) => row.id,
        // Ids are decimal strings, so the default comparator would order them
        // lexicographically ("10" before "9").
        sortFn: (a, b) => (BigInt(a.id) < BigInt(b.id) ? -1 : BigInt(a.id) > BigInt(b.id) ? 1 : 0),
        // The registry shows the global shift id, zero-padded — unlike the truck
        // card, which numbers a truck's shifts within its day (`shiftNumber`).
        cell: (info) => (
          <span className="text-sm">{`S${String(info.getValue()).padStart(5, "0")}`}</span>
        ),
      },
      {
        id: "status",
        header: t(Namespace.Autopark, "table.shifts.status"),
        width: 172,
        sortable: true,
        accessor: (row) => row.status,
        sortFn: (a, b) => getStatusPriority(a.status) - getStatusPriority(b.status),
        cell: (info) => (
          <ShiftStatusBadge
            status={info.getValue() as ConnectProto.DriverOnShiftStatus}
            ttnApproved={info.row.original.ttnApproved}
          />
        ),
      },
      {
        id: "driverName",
        header: t(Namespace.Autopark, "table.shifts.driver"),
        sortable: true,
        accessor: (row) => row.driverName,
        cell: (info) => {
          const row = info.row.original;
          // A shift can be created before anyone is assigned to it.
          const name =
            (info.getValue() as string)?.trim() || t(Namespace.Autopark, "table.shifts.noDriver");
          const truck = row.truckId != null ? trucksById.get(String(row.truckId)) : undefined;

          // Nobody swaps the driver of a shift that is over.
          if (!driverChange || !truck || isShiftTerminal(row.status))
            return <span className="text-sm">{name}</span>;

          return (
            <ShiftDriverModal
              row={row}
              truck={truck}
              change={driverChange}
              trigger={
                <TriggerHyperlink title={t(Namespace.Autopark, "modals.changeDriver.title")}>
                  <span className="text-sm">{name}</span>
                </TriggerHyperlink>
              }
            />
          );
        },
      },
      {
        id: "plateNumber",
        header: t(Namespace.Autopark, "table.shifts.plate"),
        width: 120,
        sortable: true,
        accessor: (row) => row.plateNumber,
        cell: (info) => (
          <ShiftPlate
            plateNumber={info.row.original.plateNumber}
            truckId={info.row.original.truckId}
            onOpen={onPlateClick}
          />
        ),
      },
      {
        id: "model",
        header: t(Namespace.Autopark, "table.shifts.model"),
        width: 180,
        sortable: true,
        accessor: (row) => row.model,
        cell: (info) => <span className="text-sm">{info.getValue() as string}</span>,
      },
      {
        id: "type",
        header: t(Namespace.Autopark, "table.shifts.type"),
        width: 140,
        sortable: true,
        accessor: (row) => shiftTypeLabel(row.type, t),
        cell: (info) => <span className="text-sm">{info.getValue() as string}</span>,
      },
      {
        id: "mileagePerShift",
        header: t(Namespace.Autopark, "table.shifts.mileagePerShift"),
        width: 168,
        sortable: true,
        accessor: (row) => row.usage?.mileageKm,
        cell: (info) => (
          <UsageCell
            value={info.getValue() as number | undefined}
            unit={t(Namespace.Autopark, "units.km")}
          />
        ),
      },
      {
        id: "refuel",
        header: t(Namespace.Autopark, "table.shifts.refuel"),
        width: 140,
        sortable: true,
        accessor: (row) => row.usage?.fuelRefueledLitres,
        cell: (info) => (
          <UsageCell
            value={info.getValue() as number | undefined}
            unit={t(Namespace.Autopark, "units.l")}
            sign="+"
          />
        ),
      },
      {
        // The one fuel-consumption column, in l/100 km. The id predates that and
        // saved column layouts are keyed by it.
        id: "consumptionPerKm",
        header: t(Namespace.Autopark, "table.shifts.consumption"),
        width: 160,
        sortable: true,
        // Derived client-side — the backend deliberately ships the two figures
        // and leaves the ratio to whoever displays it.
        accessor: (row) => fuelPerDistance(row.usage?.fuelConsumedLitres, row.usage?.mileageKm),
        cell: (info) => (
          <UsageCell
            value={info.getValue() as number | undefined}
            unit={t(Namespace.Autopark, "units.lPer100Km")}
            fractionDigits={1}
          />
        ),
      },
      {
        id: "twStartAt",
        header: t(Namespace.Autopark, "table.shifts.planStart"),
        width: 200,
        sortable: true,
        accessor: (row) => row.twStartAt,
        cell: (info) => (
          <DateTimeCell value={info.getValue() as string | undefined} locale={dateFnsLocale} muted />
        ),
      },
      {
        id: "waybill",
        header: t(Namespace.Autopark, "table.shifts.waybillShort"),
        width: 72,
        align: "center" as const,
        sortable: false,
        accessor: () => null,
        cell: (info) => (
          <Button
            id={`shifts-table-print-waybill-${info.row.original.id}-button`}
            variant="ghost"
            size="sm"
            className="h-8 w-8 !p-0"
            aria-label={t(Namespace.Autopark, "shifts.printWaybill")}
            title={t(Namespace.Autopark, "shifts.printWaybill")}
            onClick={() => onPrintWaybill?.(info.row.original)}
          >
            <IcPrint className="h-4 w-4 text-icon-action" />
          </Button>
        ),
      },
      {
        // Fact start, or the button that produces it — the action lives in the
        // column whose value it fills in.
        id: "startedAt",
        header: t(Namespace.Autopark, "table.shifts.releaseAt"),
        width: 200,
        sortable: true,
        accessor: (row) => row.startedAt,
        cell: (info) => {
          const row = info.row.original;
          if (!isShiftPlanned(row.status))
            return (
              <DateTimeCell value={info.getValue() as string | undefined} locale={dateFnsLocale} />
            );

          // The driver view has no driver column, so a shift still waiting for one
          // is assigned from here — starting it would be refused anyway.
          const truck = row.truckId != null ? trucksById.get(String(row.truckId)) : undefined;
          if (view === "driver" && row.driverId == null && driverChange && truck)
            return (
              <ShiftDriverModal
                row={row}
                truck={truck}
                change={driverChange}
                trigger={
                  <Button
                    id={`shifts-table-assign-driver-${row.id}-button`}
                    size="sm"
                    className="!h-6 rounded-lg px-4 text-xs font-medium"
                  >
                    {t(Namespace.Autopark, "shifts.assignDriver")}
                  </Button>
                }
              />
            );

          return (
            <Button
              id={`shifts-table-approve-${row.id}-button`}
              size="sm"
              className="!h-6 rounded-lg px-4 text-xs font-medium"
              onClick={() => onApproveShift?.(row)}
              disabled={isShiftBusy?.(row.id)}
            >
              {t(Namespace.Autopark, "shifts.start")}
            </Button>
          );
        },
      },
      {
        id: "twEndAt",
        header: t(Namespace.Autopark, "table.shifts.planEnd"),
        width: 200,
        sortable: true,
        accessor: (row) => row.twEndAt,
        cell: (info) => (
          <DateTimeCell value={info.getValue() as string | undefined} locale={dateFnsLocale} muted />
        ),
      },
      {
        // Fact completion, or the button that produces it. A planned shift has
        // neither — it has to start before it can finish.
        id: "completedAt",
        header: t(Namespace.Autopark, "table.shifts.completedAt"),
        width: 200,
        sortable: true,
        accessor: (row) => row.completed_at,
        cell: (info) => {
          const row = info.row.original;
          if (isShiftPlanned(row.status) || isShiftTerminal(row.status))
            return (
              <DateTimeCell value={info.getValue() as string | undefined} locale={dateFnsLocale} />
            );

          return (
            <Button
              id={`shifts-table-accept-${row.id}-button`}
              size="sm"
              className="!h-6 rounded-lg px-4 text-xs font-medium !bg-brand-green !text-white"
              disabled={isShiftBusy?.(row.id)}
              onClick={() => onCompleteShift?.(row)}
            >
              {isCompletingShift?.(row.id)
                ? t(Namespace.Autopark, "shifts.finishing")
                : t(Namespace.Autopark, "shifts.finish")}
            </Button>
          );
        },
      },
      {
        // Fact start to fact end; a shift that has not finished has no length yet.
        id: "duration",
        header: t(Namespace.Autopark, "table.shifts.duration"),
        width: 160,
        sortable: true,
        accessor: (row) => shiftDurationMinutes(row.startedAt, row.completed_at),
        cell: (info) => {
          const minutes = info.getValue() as number | undefined;
          return (
            <span className="text-sm">
              {minutes == null ? NO_DATA : formatShiftDuration(minutes, t, lang)}
            </span>
          );
        },
      },
    ];
    // Each view hides the columns its group headers already name.
    return columns.filter((column) => !SHIFT_VIEWS[view].hiddenColumns.has(column.id));
  }, [
    t,
    dateFnsLocale,
    lang,
    onApproveShift,
    onCompleteShift,
    onPrintWaybill,
    isShiftBusy,
    isCompletingShift,
    driverChange,
    onPlateClick,
    trucksById,
    view,
  ]);
};

/** Same figures as the on-screen row, plus the truck the group header carries. */
const exportDateTime = (value?: string) => (value ? new Date(value).toLocaleString() : NO_DATA);

export const getShiftsExportColumns = (
  t: ReturnType<typeof useT>,
  /** UI language — the duration's hour word is declined for it. */
  lang: `${Language}`,
): Column<ShiftData>[] => [
  {
    id: "id",
    header: t(Namespace.Autopark, "table.shifts.id"),
    accessor: (row) => `S${String(row.id).padStart(5, "0")}`,
  },
  {
    id: "status",
    header: t(Namespace.Autopark, "table.shifts.status"),
    accessor: (row) => shiftStatusLabel(row.status, t, row.ttnApproved),
  },
  // The screen carries plate, type and model on the group header; a flat export
  // has to repeat them per row to stay readable.
  {
    id: "plateNumber",
    header: t(Namespace.Autopark, "table.shifts.plate"),
    accessor: (row) => row.plateNumber,
  },
  {
    id: "type",
    header: t(Namespace.Autopark, "table.shifts.type"),
    accessor: (row) => shiftTypeLabel(row.type, t),
  },
  {
    id: "model",
    header: t(Namespace.Autopark, "table.shifts.model"),
    accessor: (row) => row.model,
  },
  {
    id: "driverName",
    header: t(Namespace.Autopark, "table.shifts.driver"),
    accessor: (row) => row.driverName,
  },
  {
    id: "mileagePerShift",
    header: t(Namespace.Autopark, "table.shifts.mileagePerShift"),
    accessor: (row) => formatUsage(row.usage?.mileageKm, t(Namespace.Autopark, "units.km")),
  },
  {
    id: "refuel",
    header: t(Namespace.Autopark, "table.shifts.refuel"),
    accessor: (row) =>
      formatUsage(row.usage?.fuelRefueledLitres, t(Namespace.Autopark, "units.l"), { sign: "+" }),
  },
  {
    id: "consumptionPerKm",
    header: t(Namespace.Autopark, "table.shifts.consumption"),
    accessor: (row) =>
      formatUsage(
        fuelPerDistance(row.usage?.fuelConsumedLitres, row.usage?.mileageKm),
        t(Namespace.Autopark, "units.lPer100Km"),
        { fractionDigits: 1 },
      ),
  },
  {
    id: "twStartAt",
    header: t(Namespace.Autopark, "table.shifts.planStart"),
    accessor: (row) => exportDateTime(row.twStartAt),
  },
  {
    id: "startedAt",
    header: t(Namespace.Autopark, "table.shifts.releaseAt"),
    accessor: (row) => exportDateTime(row.startedAt),
  },
  {
    id: "twEndAt",
    header: t(Namespace.Autopark, "table.shifts.planEnd"),
    accessor: (row) => exportDateTime(row.twEndAt),
  },
  {
    id: "completedAt",
    header: t(Namespace.Autopark, "table.shifts.completedAt"),
    accessor: (row) => exportDateTime(row.completed_at),
  },
  {
    id: "duration",
    header: t(Namespace.Autopark, "table.shifts.duration"),
    accessor: (row) => {
      const minutes = shiftDurationMinutes(row.startedAt, row.completed_at);
      return minutes == null ? NO_DATA : formatShiftDuration(minutes, t, lang);
    },
  },
];
